import { IClinic, IPatient, IProvider } from "interfaces";
import { GoogleCalendarEvent } from "types";
import { Apis, Constants, Enums, Helpers, Schedule, Store } from "utils";

export interface IGoogleService {
  grantAccess(): void;
  accessToken(code: string): Promise<any>;
  refreshToken(): Promise<any>;
  all(): Promise<any>;
  share(calendarId: string, email: string): Promise<any>;
  info(calendarId: string): Promise<any>;
  insert(
    calendarId: string,
    serviceId: number,
    date: Date | null,
    slot: string | null,
    duration: number,
    clinic: IClinic,
    patient: IPatient,
    provider: IProvider,
    reason: string | null,
    walkIn: boolean
  ): Promise<any>;
  find(calendarId: string, eventId: string): Promise<any>;
  update(calendarId: string, eventId: string, event: GoogleCalendarEvent): Promise<any>;
  delete(calendarId: string, eventId: string): Promise<any>;
  busy(calendarId: string, date: Date): Promise<any>;
  availability(calendarId: string, date: Date | null, duration: number, startTime: number, endTime: number): Promise<any>;
}

export class GoogleService implements IGoogleService {
  /**
   * refresh the access token
   * @returns {string} access token
   */

  get sessionToken() {
    const { access_token } = Store.sessionStorage.get();
    return access_token as string;
  }

  /**
   * generate the URL to request the grant code
   */

  grantAccess() {
    window.location.replace(
      Helpers.interpolate(Constants.GOOGLE_API.AUTH, {
        clientId: Constants.GOOGLE_API.CLIENT_ID,
        redirectUri: Constants.GOOGLE_API.REDIRECT_URI,
        scope: Constants.GOOGLE_API.SCOPE,
      })
    );
  }

  /**
   * generate the access token
   * @param {string} code grant type code generated by google 0auth
   */

  async accessToken(code: string) {
    const response = await Apis.google(Constants.GOOGLE_API.AUTH_ACCESS_TOKEN, {
      code,
      clientId: Constants.GOOGLE_API.CLIENT_ID,
      redirectUri: Constants.GOOGLE_API.REDIRECT_URI,
      secret: Constants.GOOGLE_API.SECRET_KEY,
    }).post("");
    if (response.data) {
      if ("error" in response.data && response.data.error === Enums.EnumGoogleResponse.InvalidGrant) {
        this.grantAccess();
      } else {
        const { access_token, refresh_token } = response.data;
        Store.sessionStorage.set({ access_token });
      }
    }
  }

  /**
   * renew the access token
   */

  async refreshToken() {
    const response = await Apis.google(Constants.GOOGLE_API.AUTH_REFRESH_TOKEN, {
      clientId: Constants.GOOGLE_API.CLIENT_ID,
      secret: Constants.GOOGLE_API.SECRET_KEY,
      refreshToken: Constants.GOOGLE_API.REFRESH_TOKEN,
    }).post("");
    if (response.data) {
      const { access_token } = response.data;
      Store.sessionStorage.set({ access_token });
    }
  }

  /**
   * fetch all the calendars
   * @returns {GoogleCalendar[]} list of calendars
   */

  async all() {
    try {
      await this.#handleRequest();
      const response = await Apis.calendar(Constants.GOOGLE_API.CALENDAR_LIST, this.sessionToken).get("");
      return response.data ? response.data.items : null;
    } catch (err) {
      return this.#handleError(err);
    }
  }

  /**
   * create a new calendar
   * @param {string} summary the choosen name for the calendar
   * @param {number} clinicId clinic id
   * @param {number} providerId provider id
   * @returns {string} email of the owner
   */

  async create(summary: string, clinicId: number, providerId: number) {
    try {
      await this.#handleRequest();
      const response = await Apis.calendar(Constants.GOOGLE_API.CALENDAR_INSERT, this.sessionToken).post("", { summary });
      if (response.data) {
        return response.data.id;
      }
    } catch (err) {
      return this.#handleError(err);
    }
  }

  /**
   * sent an email to share a given calendar
   * @param {string} calendarId calendar id provided by google calendar api
   * @param {string} email email of the user that the calendar should be shared with
   */

  async share(calendarId: string, email: string) {
    try {
      await this.#handleRequest();
      await Apis.calendar(Constants.GOOGLE_API.CALENDAR_INFO, this.sessionToken, { calendarId }).post("", {
        scope: {
          type: "user",
          value: email,
        },
        role: "owner",
      });
    } catch (err) {
      return this.#handleError(err);
    }
  }

  /**
   * fetch the email of the calendar's owner
   * @param {string} calendarId calendar id provided by google calendar api
   * @returns {string} email of the owner
   */

  async info(calendarId: string) {
    try {
      await this.#handleRequest();
      const response = await Apis.calendar(Constants.GOOGLE_API.CALENDAR_INFO, this.sessionToken, { calendarId }).get("");
      return response.data ? response.data.items[1].scope.value : null;
    } catch (err) {
      return this.#handleError(err);
    }
  }

  /**
   * insert an event on the calendar
   * @param {string} calendarId calendar id provided by google calendar api
   * @param {number} serviceId the id of the service
   * @param {Date} date date of the appointment
   * @param {string} slot time slot of the appointment
   * @param {IClinic} clinic clinic object
   * @param {IPatient} patient patient object
   * @param {IProvider} provider provider object
   * @returns {string} id of the event inserted or null if failed
   */

  async insert(
    calendarId: string,
    serviceId: number,
    date: Date | null,
    slot: string | null,
    duration: number,
    clinic: IClinic,
    patient: IPatient,
    provider: IProvider,
    reason: string | null,
    walkIn: boolean = false
  ) {
    await this.#handleRequest();
    const event = {} as GoogleCalendarEvent;
    event.locked = true;
    // TODO: must be improved
    // BEGIN
    event.location = "QUBE MEDICAL ™, University Avenue, Toronto, ON, Canada";
    event.summary = `${
      walkIn || serviceId === Enums.EnumService.WalkInClinic ? "WALK IN CLINIC" : serviceId === Enums.EnumService.PhysicalHealth ? "PHYSICAL HEALTH" : "MENTAL HEALTH"
    } - QUBE MEDICAL for ${Helpers.replaceComma(patient.username)} ${patient.phone}`;
    event.description = `Staff Member: ${Helpers.replaceComma(provider.username)}\nClient's phone number: ${patient.phone}\nClient's email: ${
      patient.email
    }\nReason for Visit: ${reason}\n`;
    // END
    const timeZone = Helpers.getTimeZoneDescription();
    const { start, end } = Helpers.toDateStartEnd(date, slot, duration);
    event.start = { dateTime: start, timeZone };
    event.end = { dateTime: end, timeZone };

    try {
      const response = await Apis.calendar(Constants.GOOGLE_API.CALENDAR_EVENT_INSERT, this.sessionToken, { calendarId }).post("", event);
      if (response && response.data) {
        const { id } = response.data;
        const [st, en] = [new Date(start), new Date(end)];
        Helpers.addTimeZoneOffset(st);
        Helpers.addTimeZoneOffset(en);
        return [id, st, en];
      }
    } catch (err) {
      return this.#handleError(err);
    }
  }

  /**
   * fetch a specific event from the calendar
   * @param {string} calendarId calendar id provided by google calendar api
   * @param {string} eventId event id provided by google calendar api
   * @returns {GoogleCalendarEvent} event calendar object or null if not found
   */

  async find(calendarId: string, eventId: string) {
    try {
      await this.#handleRequest();
      const response = await Apis.calendar(Constants.GOOGLE_API.CALENDAR_EVENT_MANAGE, this.sessionToken, { calendarId, eventId }).get("");
      return response.data ? response.data : null;
    } catch (err) {
      return this.#handleError(err);
    }
  }

  /**
   * update a specific event in the calendar
   * @param {string} calendarId calendar id provided by google calendar api
   * @param {string} eventId event id provided by google calendar api
   * @returns {boolean} true if successful and false otherwise
   */

  async update(calendarId: string, eventId: string, event: GoogleCalendarEvent) {
    try {
      await this.#handleRequest();
      const response = await Apis.calendar(Constants.GOOGLE_API.CALENDAR_EVENT_MANAGE, this.sessionToken, { calendarId, eventId }).put("", event);
      return response.data ? true : false;
    } catch (err) {
      return this.#handleError(err);
    }
  }

  /**
   * delete a specific event from the calendar
   * @param {string} calendarId calendar id provided by google calendar api
   * @param {string} eventId event id provided by google calendar api
   * @returns {boolean} true if successful and false otherwise
   */

  async delete(calendarId: string, eventId: string) {
    try {
      await this.#handleRequest();
      const response = await Apis.calendar(Constants.GOOGLE_API.CALENDAR_EVENT_MANAGE, this.sessionToken, { calendarId, eventId }).delete("");
      return response.data ? true : false;
    } catch (err) {
      return this.#handleError(err);
    }
  }

  /**
   * fetch the busy slots for a specific date
   * @param {string} calendarId calendar id provided by google calendar api
   * @param {Date} date date where the slots will be fetched
   * @returns {boolean} the list of available slots
   */

  async busy(calendarId: string, date: Date) {
    try {
      await this.#handleRequest();
      const response = await Apis.calendar(Constants.GOOGLE_API.CALENDAR_FREE_BUSY, this.sessionToken).post("", {
        timeMin: Helpers.toISOString(date, "00:00:00"),
        timeMax: Helpers.toISOString(date, "23:59:59"),
        items: [
          {
            id: calendarId,
          },
        ],
      });
      return response.data ? response.data.calendars[calendarId].busy : null;
    } catch (err) {
      return this.#handleError(err);
    }
  }

  /**
   * fetch all the available time slots for a specific date
   * @param {string} calendarId the id of the calendar to fetch
   * @param {Date | null} date the date where the slots will be fetched
   * @param {number} duration the length of the slot
   * @param {number} startTime the start time of the shift
   * @param {number} endTime the end time of the shift
   * @return list of available slots
   */

  async availability(calendarId: string, date: Date | null, duration: number, startTime: number, endTime: number) {
    if (!date) return [];
    const shift = this.#dayShift(date);
    const reduced = this.#reduceArray(await this.busy(calendarId, date), duration, shift.start * 60, shift.end * 60);
    const slots = [];

    const min = Helpers.compareTo(date, new Date()) ? Helpers.toSeconds(new Date(date.setTime(new Date().getTime()))) / 60 : 0;

    for (let i = shift.start * 60; i < shift.end * 60; i += duration) {
      if (i > min) {
        const ampm = Helpers.toTimeAmPmString(i);

        if (!reduced.includes(i) && !reduced.includes(i + duration - 5)) {
          slots.push(ampm);
        }
      }
    }

    return slots.map((item: string) => ({ label: item }));
  }

  /**
   * function to treat the return from the availability request and reduce the values
   * @param {Array} events events on a specific date
   * @return list of busy slots
   */

  #reduceArray(events: any, duration: number, startTime: number, endTime: number) {
    if (!events || (events && events.length === 0)) return [];
    /**
     * only the start time is not considered due to the necessity of allowing the overlap of the end time
     * only the times that overlaps the possible slots (considering the length) are included to enhance performance
     */
    return events
      .map((item: { start: string; end: string }) => {
        const busy = [];
        let date = new Date(item.start);
        const endDate = new Date(item.end);
        const timezoneOffset = date.getTimezoneOffset();
        // excludes the end time
        while (date < endDate) {
          // it converts time to string e.g. x timestamp => 10:00:00
          const timeString = Helpers.toTimeString(date.getTime() / 1000 - timezoneOffset * 60);
          // it converts the time string to minutes e.g. 10:00:00 => 600
          const minutes = Helpers.toMinutes(timeString);
          // it verifies if the minutes are multiple of the timespan defined
          if (minutes % 5 === 0) {
            busy.push(minutes);
          }
          date.setMinutes(date.getMinutes() + 1);
        }
        return busy.join();
      })
      .join();
  }

  /**
   * it returns the start and end times for the shift on a specific date
   * @param {Date} date date to be checked
   * @returns
   */

  #dayShift(date: Date) {
    let shift = null;
    Object.keys(Schedule.EXCEPTION_SCHEDULE).forEach((item: string) => {
      if (Helpers.compareTo(new Date(item), date)) {
        shift = Schedule.EXCEPTION_SCHEDULE[item];
        return false;
      }
    });

    return shift ?? (Constants.DEFAULT_SCHEDULE as any)[date.getDay()];
  }

  /**
   * handle the functions before the main request
   */

  async #handleRequest() {
    await this.refreshToken();
  }

  /**
   * handle possible erros from the API
   * @param {any} err error object from the server
   * @returns {null} nothing
   */

  #handleError(err: any) {
    console.log(err);
  }
}
